import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map } from 'rxjs';

import { SegmentTrackEvent } from '@ninety/mobile/platform/services';
import { SegmentActions } from '@ninety/mobile/platform/state';

import { CreateDiscussionEffectsActions, DiscussionFormEffectsActions } from '../actions';

/**
 * Engagement analytics for reviews (mobile "discussions"). Mirrors ng-ninety's
 * `pa_review_created` / `pa_review_completed`. Review creation fires here regardless of entry
 * point (People/discussions page or global create) because both dispatch `createDiscussionClicked`.
 *
 * `reviewType` (`quarterly` | `annual`) distinguishes the two review cadences without splitting the
 * shared `review` entityType — a sub-variant is a param, not a new entity.
 */
@Injectable()
export class DiscussionAnalyticsEffects {
  private readonly actions$ = inject(Actions);

  /** Track when a user creates a review. */
  trackReviewCreated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateDiscussionEffectsActions.createDiscussionSucceeded),
      map(({ discussion }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.PA_REVIEW_CREATED,
          params: {
            entityId: discussion._id,
            reviewType: discussion.type,
          },
        })
      )
    )
  );

  /**
   * Track when a user completes a review (submits the form as finished). `discussionType` rides on
   * the `discussionFormSaveSucceeded` action payload (sampled from the form's discussion at save
   * time), so no store read is needed here.
   */
  trackReviewCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DiscussionFormEffectsActions.discussionFormSaveSucceeded),
      filter(({ finished }) => finished),
      map(({ _id, discussionType }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.PA_REVIEW_COMPLETED,
          params: {
            entityId: _id,
            reviewType: discussionType,
          },
        })
      )
    )
  );
}
